import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Upload } from 'lucide-react';
import { fixedAssetsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import AssetPageHeader from '../../components/assets/AssetPageHeader';
import AssetCategoryForm from '../../components/assets/AssetCategoryForm';
import AssetStatusBadge from '../../components/assets/AssetStatusBadge';

const initial = {
  name: '', asset_number_prefix: 'AST', category_type: 'tangible_fixed_asset', useful_life_months: 60,
  depreciation_method: 'straight_line', depreciation_frequency: 'monthly', depreciation_convention: 'following_month',
  residual_value_method: 'percentage', residual_value_amount: 0, residual_value_percentage: 0,
  is_depreciable: true, is_active: true, accounting_mappings: {}, requirements: {}, controls: {},
};

export default function AssetCategories() {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const load = async () => {
    try {
      const [categories, suggestions] = await Promise.all([
        fixedAssetsAPI.categories.list(),
        fixedAssetsAPI.setup.accountSuggestions(),
      ]);
      setRows(categories.data || []);
      setAccounts(suggestions.data?.all_accounts || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load asset categories and accounts');
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Enter the category name'); return; }
    if (!form.asset_number_prefix.trim()) { toast.error('Enter the asset number prefix'); return; }
    if (form.is_depreciable !== false) {
      const mappings = form.accounting_mappings || {};
      if (!mappings.asset_cost_account_id || !mappings.accumulated_depreciation_account_id || !mappings.depreciation_expense_account_id) {
        toast.error('Select the asset cost, accumulated depreciation and depreciation expense accounts');
        return;
      }
    } else if (!form.accounting_mappings?.asset_cost_account_id) {
      toast.error('Select the asset cost account');
      return;
    }
    try {
      await fixedAssetsAPI.categories.create(form);
      toast.success('Asset category created with an automatic code');
      setOpen(false);
      setForm(initial);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create category');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this unused category?')) return;
    try {
      await fixedAssetsAPI.categories.remove(id);
      toast.success('Category deleted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete category');
    }
  };

  const importRows = async () => {
    if (!importFile) { toast.error('Select a CSV or XLSX file first'); return; }
    try {
      const response = await fixedAssetsAPI.imports.upload('categories', importFile);
      toast.success(response.data?.message || 'Categories imported');
      setImportFile(null);
      const input = document.getElementById('asset-category-import');
      if (input) input.value = '';
      load();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Import validation failed');
    }
  };

  return (
    <div className="space-y-6">
      <AssetPageHeader
        title="Asset Categories"
        description="Category codes are generated automatically. Configure useful lives, depreciation policy and general-ledger mappings for each asset class."
        actions={(
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />New category</Button></DialogTrigger>
            <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
              <DialogHeader><DialogTitle>Create asset category</DialogTitle></DialogHeader>
              <AssetCategoryForm value={form} onChange={setForm} accounts={accounts} />
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <Card><CardContent className="flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-64 flex-1"><Label>Bulk import categories</Label><Input id="asset-category-import" type="file" accept=".csv,.xlsx" onChange={(event) => setImportFile(event.target.files?.[0] || null)} /></div>
        <Button variant="outline" onClick={importRows}><Upload className="mr-2 h-4 w-4" />Validate and import</Button>
        <p className="w-full text-xs text-slate-500">CSV/XLSX rows are fully validated before insertion. Codes omitted from imported rows are generated automatically.</p>
      </CardContent></Card>

      <Card><CardContent className="p-0"><div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Useful life</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{rows.length ? rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="font-bold">{row.code}</TableCell><TableCell>{row.name}</TableCell><TableCell>{row.category_type?.replaceAll('_', ' ')}</TableCell>
              <TableCell>{row.useful_life_months} months</TableCell><TableCell>{row.depreciation_method?.replaceAll('_', ' ')}</TableCell>
              <TableCell><AssetStatusBadge status={row.is_active ? 'active' : 'inactive'} /></TableCell>
              <TableCell><Button size="icon" variant="ghost" onClick={() => remove(row.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
            </TableRow>
          )) : <TableRow><TableCell colSpan={7} className="h-28 text-center text-slate-500">No asset categories created.</TableCell></TableRow>}</TableBody>
        </Table>
      </div></CardContent></Card>
    </div>
  );
}
